/**
 * Login Screen Component
 *
 * Provides email/password and guest authentication for the unified board game client.
 *
 * Interface contract: docs/wireframes/auth/unified-login.md (W-B variant).
 * The guest button is the ONLY allowed deviation from the common card (W-A),
 * and only on board-game/stag-board-game — do not copy it to other surfaces.
 */

import { useState } from "react";
import { navigateTo } from "../../router";
import { Alert, Button, Card, Input } from "../../ui";
import { getConfig } from "../config/runtime-config";
import { useAuth, useAuthActions } from "../store/auth-store";
import { GoogleLoginButton } from "./GoogleLoginButton";

export function LoginScreen() {
  const { isLoading, error } = useAuth();
  const actions = useAuthActions();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const REGISTER_ENABLED = getConfig().enableRegister;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const success = await actions.login(email, password);
    if (success) {
      navigateTo("/");
    }
  };

  const handleGoogleLogin = async (idToken: string) => {
    const success = await actions.loginWithGoogle(idToken);
    if (success) {
      navigateTo("/");
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center p-3 sm:p-6">
      <Card className="w-full shadow-lg sm:max-w-md sm:p-8">
        <h1 className="mb-2 text-center font-semibold text-2xl">로그인</h1>
        <p className="mb-6 text-center text-sm text-text-secondary">
          보드게임 플랫폼에 오신 것을 환영합니다
        </p>

        {error && <Alert className="mb-4">{error}</Alert>}

        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <Input
            type="email"
            placeholder="이메일"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={isLoading}
          />
          <Input
            type="password"
            placeholder="비밀번호"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={isLoading}
          />
          <Button type="submit" data-testid="login-btn" disabled={isLoading}>
            {isLoading ? "로그인 중..." : "로그인"}
          </Button>
        </form>

        <div className="mt-4 flex items-center gap-3">
          <div className="h-px flex-1 bg-border-subtle" />
          <span className="text-text-hint text-xs">또는</span>
          <div className="h-px flex-1 bg-border-subtle" />
        </div>

        <div className="mt-4">
          <GoogleLoginButton
            onSuccess={handleGoogleLogin}
            disabled={isLoading}
          />
        </div>

        <Button
          variant="secondary"
          data-testid="guest-login-btn"
          onClick={() => navigateTo("/guest-login")}
          disabled={isLoading}
          className="mt-4 w-full"
        >
          게스트로 시작
        </Button>

        {REGISTER_ENABLED && (
          <p className="mt-4 text-center text-sm text-text-secondary">
            계정이 없으신가요?{" "}
            <Button
              variant="ghost"
              size="sm"
              data-testid="register-link"
              onClick={() => navigateTo("/register")}
              className="px-0 py-0"
            >
              회원가입
            </Button>
          </p>
        )}
      </Card>
    </div>
  );
}
